/**
 * The shape of the phone the panel draws.
 *
 * The picture box follows the real screen — its aspect ratio from the frame
 * itself, its corner rounding from what the phone reports — so nothing is
 * letterboxed, stretched, or hidden behind a curve the phone does not have.
 * Kept out of the component so the fallbacks are testable on their own.
 */

export interface Size {
  width: number;
  height: number;
}

/** Width ÷ height of a typical phone in portrait, before anything is known. */
export const DEFAULT_ASPECT = 9 / 19.5;

/** A corner can be no rounder than a semicircle across the short edge. */
const MAX_CORNER = 0.5;

/**
 * Width over height of the box to draw. The last frame's own pixels win —
 * they follow rotation — then what the phone declared at enrollment, then a
 * typical phone until something better arrives.
 */
export function screenAspect(
  frame: Size | null,
  declared: { screen_width: number | null; screen_height: number | null } | null | undefined,
): number {
  if (frame && frame.width > 0 && frame.height > 0) return frame.width / frame.height;
  const width = declared?.screen_width ?? 0;
  const height = declared?.screen_height ?? 0;
  if (width > 0 && height > 0) return width / height;
  return DEFAULT_ASPECT;
}

/**
 * The phone's own corner rounding, as a fraction of the screen's short edge
 * (the agent measures it; a Pixel is around 0.09). Unknown or nonsense means
 * square: the panel shows every pixel rather than hide some behind a guess.
 */
export function cornerFraction(reported: unknown): number {
  if (typeof reported !== "number" || !Number.isFinite(reported) || reported <= 0) return 0;
  return Math.min(reported, MAX_CORNER);
}
